import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Sliding image carousel: every slide is rendered at the same fixed height with the
// track translated horizontally, so mixed-aspect photos never resize the container.
function ImageCarousel({ images, alt, height, objectFit }) {
  const [index, setIndex] = useState(0);
  const total = images.length;

  // Reset when a different set of photos is shown (e.g. another vehicle in the dialog).
  useEffect(() => {
    setIndex(0);
  }, [images]);

  if (total === 0) return null;

  const goTo = (next) => setIndex(((next % total) + total) % total);

  const handleKeyDown = (event) => {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      goTo(index + 1);
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      goTo(index - 1);
    }
  };

  const arrowStyles = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    backgroundColor: "rgba(0, 0, 0, 0.45)",
    color: "#fff",
    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.65)" },
  };

  return (
    <MDBox mb={2}>
      <MDBox
        position="relative"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        sx={{
          height,
          overflow: "hidden",
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "grey.100",
          outline: "none",
        }}
      >
        <MDBox
          display="flex"
          height="100%"
          sx={{
            transform: `translateX(-${index * 100}%)`,
            transition: "transform 300ms ease",
          }}
        >
          {images.map((image, idx) => (
            <MDBox
              key={image + idx}
              component="img"
              src={image}
              alt={`${alt} photo ${idx + 1}`}
              referrerPolicy="no-referrer"
              flexShrink={0}
              sx={{ width: "100%", height: "100%", objectFit, display: "block" }}
            />
          ))}
        </MDBox>

        {total > 1 && (
          <>
            <IconButton
              aria-label="Previous photo"
              size="small"
              onClick={() => goTo(index - 1)}
              sx={{ ...arrowStyles, left: 8 }}
            >
              <Icon>chevron_left</Icon>
            </IconButton>
            <IconButton
              aria-label="Next photo"
              size="small"
              onClick={() => goTo(index + 1)}
              sx={{ ...arrowStyles, right: 8 }}
            >
              <Icon>chevron_right</Icon>
            </IconButton>

            <MDBox
              position="absolute"
              bottom={8}
              right={8}
              px={1}
              py={0.25}
              sx={{ backgroundColor: "rgba(0, 0, 0, 0.55)", borderRadius: 1 }}
            >
              <MDTypography variant="caption" sx={{ color: "#fff" }}>
                {index + 1} / {total}
              </MDTypography>
            </MDBox>
          </>
        )}
      </MDBox>

      {total > 1 && (
        <MDBox display="flex" gap={1} mt={1} sx={{ overflowX: "auto" }}>
          {images.map((image, idx) => (
            <MDBox
              key={`thumb-${image}-${idx}`}
              component="img"
              src={image}
              alt={`${alt} thumbnail ${idx + 1}`}
              referrerPolicy="no-referrer"
              onClick={() => goTo(idx)}
              flexShrink={0}
              sx={{
                width: 64,
                height: 48,
                objectFit: "contain",
                backgroundColor: "grey.100",
                borderRadius: 1,
                cursor: "pointer",
                border: "2px solid",
                borderColor: idx === index ? "info.main" : "transparent",
                opacity: idx === index ? 1 : 0.6,
                transition: "opacity 150ms ease",
                "&:hover": { opacity: 1 },
              }}
            />
          ))}
        </MDBox>
      )}
    </MDBox>
  );
}

ImageCarousel.defaultProps = {
  alt: "",
  height: 260,
  objectFit: "contain",
};

ImageCarousel.propTypes = {
  images: PropTypes.arrayOf(PropTypes.string).isRequired,
  alt: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  objectFit: PropTypes.oneOf(["cover", "contain"]),
};

export default ImageCarousel;
